import PropTypes from 'prop-types';
import React from 'react';

import { FlatList, View, StyleSheet, Keyboard, Pressable, Text } from 'react-native';

import { Navigation } from '../../components';

import LoadEarlier from './LoadEarlier';
import Message from './Message';
import Color from './Color';

import { GROUP_MESSAGE } from '../../../constants/objectTypes';

export default class MessageContainer extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      showScrollBottom: false,
    };

    this._attachmentConfigs = this._attachmentConfigsFromMessages(
      props.messages,
      props.directChatMode
    );
  }

  componentDidMount() {
    if (this.props.messages.length === 0) {
      this.attachKeyboardListeners();
    }
  }

  componentWillUnmount() {
    this.detachKeyboardListeners();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.messages.length === 0 && this.props.messages.length > 0) {
      this.detachKeyboardListeners();
    } else if (prevProps.messages.length > 0 && this.props.messages.length === 0) {
      this.attachKeyboardListeners(this.props);
    }

    if (this.props.messages !== prevProps.messages) {
      this._attachmentConfigs = this._attachmentConfigsFromMessages(
        this.props.messages,
        this.props.directChatMode
      );
    }
  }

  showFullScreenImage = (attachment) => {
    Navigation.showModal({
      screen: 'FullscreenImageModal',
      passProps: {
        attachments: this._attachmentConfigs,
        attachment,
      },
    });
  };

  attachKeyboardListeners = () => {
    const { invertibleScrollViewProps: invertibleProps } = this.props;

    if (this.keyboardEventListeners?.length) this.detachKeyboardListeners();

    this.keyboardEventListeners = [
      Keyboard.addListener('keyboardWillShow', invertibleProps.onKeyboardWillShow),
      Keyboard.addListener('keyboardDidShow', invertibleProps.onKeyboardDidShow),
      Keyboard.addListener('keyboardWillHide', invertibleProps.onKeyboardWillHide),
      Keyboard.addListener('keyboardDidHide', invertibleProps.onKeyboardDidHide),
    ];
  };

  detachKeyboardListeners = () => {
    if (!this.keyboardEventListeners?.length) return;

    this.keyboardEventListeners.forEach((eventListener) => eventListener.remove());
  };

  renderFooter = () => {
    if (this.props.renderFooter) {
      const footerProps = {
        ...this.props,
      };
      return this.props.renderFooter(footerProps);
    }
    return null;
  };

  renderLoadEarlier = () => {
    if (this.props.loadEarlier === true) {
      const loadEarlierProps = {
        ...this.props,
      };
      if (this.props.renderLoadEarlier) {
        return this.props.renderLoadEarlier(loadEarlierProps);
      }
      return <LoadEarlier {...loadEarlierProps} />;
    }
    return null;
  };

  scrollTo(options) {
    if (this.flatListRef && options) {
      this.flatListRef.scrollToOffset(options);
    }
  }

  scrollToIndex(options) {
    if (this.flatListRef && options) {
      this.flatListRef.scrollToIndex(options);
    }
  }

  scrollToBottom = () => {
    this.scrollTo({ offset: 0, animated: 'true' });

    this.props.handleOnScrollToBottom && this.props.handleOnScrollToBottom();
  };

  handleOnScroll = (event) => {
    if (event.nativeEvent.contentOffset.y > this.props.scrollToBottomOffset) {
      this.setState({ showScrollBottom: true });
    } else {
      this.setState({ showScrollBottom: false });
    }

    this.props.handleOnScroll && this.props.handleOnScroll(event);
  };

  _attachmentConfigsFromMessages(messages) {
    const objectType = GROUP_MESSAGE;

    return messages
      .map((message) => {
        const { messageId, image } = message;
        if (!image) return null;

        return {
          objectType,
          objectId: messageId,
          fileName: image,
        };
      })
      .filter((item) => item !== null);
  }

  renderRow = ({ item, index }) => {
    if (!item._id && item._id !== 0) {
      console.warn('GiftedChat: `_id` is missing for message', JSON.stringify(item));
    }
    if (!item.user) {
      if (!item.system) {
        console.warn('GiftedChat: `user` is missing for message', JSON.stringify(item));
      }
      item.user = {};
    }
    const { messages, ...restProps } = this.props;
    const previousMessage = messages[index + 1] || {};
    const nextMessage = messages[index - 1] || {};

    const messageProps = {
      ...restProps,
      attachments: this._attachmentConfigs,
      currentMessage: item,
      previousMessage,
      nextMessage,
      position: item.user._id === this.props.user._id ? 'right' : 'left',
      showFullScreenImage: this.showFullScreenImage,
    };

    return <Message {...messageProps} />;
  };

  renderHeaderWrapper = () => <View style={styles.headerWrapper}>{this.renderLoadEarlier()}</View>;

  renderScrollToBottomWrapper() {
    const scrollToBottomComponent = (
      <View style={styles.scrollToBottomStyle}>
        <Pressable onPress={this.scrollToBottom} hitSlop={{ top: 5, left: 5, right: 5, bottom: 5 }}>
          <Text>V</Text>
        </Pressable>
      </View>
    );

    if (this.props.scrollToBottomComponent) {
      return (
        <Pressable onPress={this.scrollToBottom} hitSlop={{ top: 5, left: 5, right: 5, bottom: 5 }}>
          {this.props.scrollToBottomComponent}
        </Pressable>
      );
    }
    return scrollToBottomComponent;
  }

  keyExtractor = (item) => `${item._id}`;

  render() {
    if (this.props.messages.length === 0) {
      return <View style={styles.container} />;
    }
    return (
      <View style={styles.container}>
        {this.state.showScrollBottom && this.props.scrollToBottom
          ? this.renderScrollToBottomWrapper()
          : null}
        <FlatList
          ref={(ref) => (this.flatListRef = ref)}
          maintainVisibleContentPosition={{
            minIndexForVisible: 0,
          }}
          initialNumToRender={this.props.initialNumToRender}
          initialScrollIndex={this.props.initialScrollIndex}
          onScrollToIndexFailed={this.props.onScrollToIndexFailed}
          onEndReached={this.props.onEndReached}
          onEndReachedThreshold={this.props.onEndReachedThreshold}
          extraData={this.props.extraData}
          keyExtractor={this.props.keyExtractor || this.keyExtractor}
          enableEmptySections
          automaticallyAdjustContentInsets={false}
          inverted={this.props.inverted}
          data={this.props.messages}
          style={styles.listStyle}
          contentContainerStyle={styles.contentContainerStyle}
          renderItem={this.renderRow}
          {...this.props.invertibleScrollViewProps}
          ListFooterComponent={this.renderHeaderWrapper}
          ListHeaderComponent={this.renderFooter}
          onScroll={this.handleOnScroll}
          scrollEventThrottle={100}
        />
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainerStyle: {
    justifyContent: 'flex-end',
  },
  headerWrapper: {
    flex: 1,
  },
  listStyle: {
    flex: 1,
  },
  scrollToBottomStyle: {
    opacity: 0.8,
    position: 'absolute',
    paddingHorizontal: 15,
    paddingVertical: 8,
    right: 10,
    bottom: 30,
    zIndex: 999,
    height: 40,
    width: 40,
    borderRadius: 20,
    backgroundColor: Color.white,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: Color.black,
    shadowOpacity: 0.5,
    shadowOffset: { width: 0, height: 0 },
    shadowRadius: 1,
  },
});

MessageContainer.defaultProps = {
  messages: [],
  user: {},
  renderFooter: null,
  onLoadEarlier: () => {},
  inverted: true,
  loadEarlier: false,
  invertibleScrollViewProps: {},
  extraData: null,
  scrollToBottom: false,
  scrollToBottomOffset: 200,
};
